import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, User, Bot, ExternalLink, X, Star } from 'lucide-react';

import chatlogo from '../Image/chatbotLogo.png'
import chatdp from '../Image/chatbotDp.png'
import { useSearchParams } from 'react-router-dom';

const handleClick = (navigation) => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  let url;

  // Detect iOS
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    url = 'https://apps.apple.com/in/app/fiydaa-fintech-hub-by-speculit/id6475651556';
  }
  // Detect Android
  else if (/android/i.test(userAgent)) {
    url = 'https://play.google.com/store/apps/details?id=com.fiydaa&pcampaignid=web_share';
  }
  // Detect macOS
  else if (navigator.platform.toLowerCase().includes('mac')) {
    url = 'https://apps.apple.com/in/app/fiydaa-fintech-hub-by-speculit/id6475651556';
  }
  // Default to Play Store for other platforms (e.g., Windows)
  else {
    url = 'https://play.google.com/store/apps/details?id=com.fiydaa&pcampaignid=web_share';
  }

  window.open(url, '_blank');
};

const generateSessionId = () => {
  const now = new Date();
  const timestamp = now.toISOString().replace(/[-:.]/g, '').slice(0, 15);
  const randomPart = Math.random().toString(36).substring(2, 10);
  return `chat_${timestamp}_${randomPart}`;
};

function FiydaaChatBot() {
  const [sessionId, setSessionId] = useState(generateSessionId())
  const [limitReached, setLimitReached] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hiddenFollowUps, setHiddenFollowUps] = useState(new Set());
  const [isFirstChat, setIsFirstChat] = useState(true);
  // Feedback modal states
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const feedbackTextRef = useRef(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const feedbackTimeoutRef = useRef(null); // Add timeout ref
  const [searchParams] = useSearchParams();
  const [source, setSource] = useState(searchParams.get('source') || 'unknown');
  // console.log(source)


  const initialQuestions = [
    "What is Digital Gold?",
    "What does Fiydaa do?",
    "Gold returns in last 1 year?"
  ];

  const handleFirstMessage = () => {
    setIsFirstChat(false);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  function getISTISOString() {
    const istTime = new Date();
    const year = istTime.getFullYear();
    const month = String(istTime.getMonth() + 1).padStart(2, '0');
    const day = String(istTime.getDate()).padStart(2, '0');
    const hours = String(istTime.getHours()).padStart(2, '0');
    const minutes = String(istTime.getMinutes()).padStart(2, '0');
    const seconds = String(istTime.getSeconds()).padStart(2, '0');
    const millis = String(istTime.getMilliseconds()).padStart(3, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${millis}`;
  }

  useEffect(() => {
    scrollToBottom();
    if (messages.length > 53 && !showFeedbackModal) {
      setLimitReached(true);
      setShowFeedbackModal(true);
    }
  }, [messages]);

  useEffect(() => {
    if (showWelcomeModal || showFeedbackModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showWelcomeModal, showFeedbackModal]);

  const parseResponse = (responseData) => {
    try {
      let answer = responseData.assistantMessage || "Sorry, I didn't get that.";
      answer = answer.replace(/\\n/g, '\n');
      answer = answer.replace(/\*\*(.*?)\*\*/g, '$1');

      let followupQuestions = responseData.followUps || [];
      followupQuestions = followupQuestions.map(question =>
        question.replace(/\\n/g, '').trim()
      );

      const ctaButtons = responseData.CTAs || [];

      return {
        answer,
        followupQuestions,
        ctaButtons
      };
    } catch (error) {
      console.error('Error parsing response:', error);
      return {
        answer: "Sorry, I didn't get that.",
        followupQuestions: [],
        ctaButtons: []
      };
    }
  };

  const sendToLambda = async (userMessage) => {
    setIsTyping(true);
    setHiddenFollowUps(prev => {
      const newHidden = new Set(prev);
      messages.forEach(msg => {
        if (msg.sender === 'assistant' && msg.followupQuestions && msg.followupQuestions.length > 0) {
          newHidden.add(msg.id);
        }
      });
      return newHidden;
    });

    try {
      const dataToSend = {
        sessionId: sessionId,
        source: source,
        userMessage: userMessage,
        assistantLimitMessage: "**Limit Reached!**\nYou have reached the maximum limit of this chat. If your query is still not resolved, please raise a ticket or email us at support@fiydaa.in.",
        conversation: messages,
        limitReached: limitReached,
        userMessageTime: getISTISOString(),
        sessionStartTime: getISTISOString(),
      };

      const response = await fetch('https://rzozy98ys9.execute-api.ap-south-1.amazonaws.com/dev/websiteApi/fiydaa_Ai_Chat_Bot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });

      const data = await response.json();
      // console.log('API Response:', data);

      const parsedResponse = parseResponse(data);

      const messageId = Date.now();
      setMessages(prev => [
        ...prev,
        {
          id: messageId,
          text: parsedResponse.answer,
          sender: "assistant",
          timestamp: new Date(),
          followupQuestions: parsedResponse.followupQuestions,
          ctaButtons: parsedResponse.ctaButtons
        }
      ]);
      handleFirstMessage();

    } catch (err) {
      console.error('Error sending message:', err);
      setMessages(prev => [
        ...prev,
        {
          id: Date.now(),
          text: "Something went wrong. Please try again.",
          sender: "assistant",
          timestamp: new Date()
        }
      ]);
    }
    setIsTyping(false);
    inputRef.current?.focus();
  };

  const submitFeedback = async () => {
    setIsSubmittingFeedback(true);

    try {
      const feedbackData = {
        sessionId: sessionId,
        rating: feedbackRating,
        feedback: feedbackText,
        conversationLength: messages.length,
        timestamp: getISTISOString(),
        type: 'limit_reached_feedback'
      };
      // console.log(feedbackData)

      const response = await fetch('https://rzozy98ys9.execute-api.ap-south-1.amazonaws.com/dev/websiteApi/fiydaa_feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackData)
      });

      const data = await response.json();
      // console.log('Feedback submitted:', data);

    } catch (error) {
      console.error('Error submitting feedback:', error);
    }

    setIsSubmittingFeedback(false);
  };

  const handleStartAgain = async () => {
    await submitFeedback();
    setShowFeedbackModal(false);
    // Reset all states to restart the chat
    setSessionId(generateSessionId());
    setLimitReached(false);
    setMessages([]);
    setInputText('');
    setIsTyping(false);
    setHiddenFollowUps(new Set());
    setIsFirstChat(true);
    setShowIntro(true);
    setShowWelcomeModal(true);
    setFeedbackRating(0);
    setFeedbackText('');
  };

  const handleVisitWebsite = async () => {
    await submitFeedback();
    // Replace with your actual website URL
    window.open('https://www.fiydaa.in', '_blank');
    handleStartAgain()
    setShowFeedbackModal(false);
  };

  const startChatWithQuestion = (question) => {
    const welcomeMessage = {
      id: 1,
      text: "Hi I am Sona! Your virtual financial assistant! How can I help you today?",
      sender: "assistant",
      timestamp: new Date()
    };

    const userMessage = {
      id: Date.now(),
      text: question,
      sender: "user",
      timestamp: new Date()
    };

    setMessages([welcomeMessage, userMessage]);
    setShowIntro(false);
    sendToLambda(question);
  };

  const startChatManually = () => {
    const welcomeMessage = {
      id: 1,
      text: "Hello! How can I help you today?",
      sender: "assistant",
      timestamp: new Date()
    };

    setMessages([welcomeMessage]);
    setShowIntro(false);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleSendMessage = () => {
    if (!inputText.trim() || inputText.length > 200) return;

    if (typeof window.fbq === 'function') {
      window.fbq('trackCustom', 'ChatMessageSent', {
        source: 'chatbot',
        text: inputText.trim(),
        timestamp: new Date().toISOString(),
        userId: sessionId || 'guest',
      });
    }

    const newMessage = {
      id: Date.now(),
      text: inputText,
      sender: "user",
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);
    sendToLambda(inputText);
    setInputText('');
    inputRef.current?.focus();
  };

  const handleFollowUpClick = (question, messageId) => {
    const newMessage = {
      id: Date.now(),
      text: question,
      sender: "user",
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);
    sendToLambda(question);
    setHiddenFollowUps(prev => new Set([...prev, messageId]));
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const closeWelcomeModal = () => {
    setShowWelcomeModal(false);
  };

  const handleGetStarted = () => {
    setShowWelcomeModal(false);
    startChatWithQuestion("How to start investing in digital gold?");
  };


  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      // Don't allow closing by clicking backdrop for feedback modal
      return;
    }
  };

  const renderStars = () => {
    return [1, 2, 3, 4, 5].map((starNumber) => (
      <button
        key={starNumber}
        onClick={() => setFeedbackRating(starNumber)}
        className={`transition-colors duration-200 ${starNumber <= feedbackRating
          ? 'text-yellow-400'
          : 'text-gray-300 hover:text-yellow-200'
          }`}
      >
        <Star className={`w-8 h-8 ${starNumber <= feedbackRating ? 'fill-current' : ''}`} />
      </button>
    ));
  };

  const FeedbackModal = useCallback(() => {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 relative overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-br from-red-500 to-orange-600 px-6 pt-6 pb-8">
            <div className="flex flex-col items-center text-center">
              <img src={chatlogo} alt="Fiydaa Logo" className="w-14 h-14 object-contain mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2 font-poppins">Chat Limit Reached!</h2>
              <p className="text-red-100 text-sm font-poppins">You've reached the maximum number of messages</p>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 font-poppins">
                How was your experience ?
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed font-poppins">
                We'd love to hear about your experience with Fiydaa-AI. Your feedback helps us improve.
              </p>
            </div>

            {/* Star Rating */}
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-3 text-center font-poppins">
                Rate your experience:
              </p>
              <div className="flex justify-center space-x-2">
                {renderStars()}
              </div>
              {feedbackRating > 0 && (
                <p className="text-center text-sm text-gray-600 mt-2 font-poppins">
                  {feedbackRating === 1 && "We're sorry to hear that"}
                  {feedbackRating === 2 && "We can do better"}
                  {feedbackRating === 3 && "Thanks for your feedback"}
                  {feedbackRating === 4 && "We're glad you had a good experience"}
                  {feedbackRating === 5 && "Awesome! Thank you!"}
                </p>
              )}
            </div>

            {/* Feedback Text */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">
                Share your thoughts :
              </label>
              <textarea
                key={1}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Tell us about your experience with digital gold queries..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-poppins"
                rows="2"
                maxLength="500"
              />
              <p className="text-xs text-gray-500 mt-1 font-poppins">
                {feedbackText.length}/500 characters
              </p>
            </div>

            {/* Action buttons */}
            <div className="space-y-3">
              <button
                onClick={handleStartAgain}
                disabled={isSubmittingFeedback}
                className="w-full py-3 font-poppins bg-[#0b2436] text-white rounded-full font-semibold border-2 border-[#cfb484] transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmittingFeedback ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  'Start Again'
                )}
              </button>
              <button
                onClick={handleVisitWebsite}
                disabled={isSubmittingFeedback}
                className="w-full py-3 border font-poppins border-[#0b2436] text-[#0b2436] rounded-full font-medium hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Visit Our Website
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }, [feedbackText, feedbackRating]);

  const WelcomeModal = () => {
    const handleBackdropClick = (e) => {
      if (e.target === e.currentTarget) {
        closeWelcomeModal();
      }
    };

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 relative overflow-hidden">
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 px-6 pt-6 pb-8">
            <div className="flex flex-col items-center text-center">
              <img src={chatlogo} alt="Fiydaa Logo" className="w-14 h-14 object-contain" />
              <h2 className="text-2xl font-bold text-white  mt-5 mb-2 font-poppins">Sona Welcomes You</h2>
              <p className="text-blue-100 text-sm font-poppins">Hi, I am Sona, your personal financial assistant</p>

            </div>
          </div>

          <div className="px-6 py-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 font-poppins">
                New to Digital Gold Investment?
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed font-poppins">
                Ask our Sona and get personalized help to start your digital gold investment journey.
                Learn about SIP, returns, and smart investment strategies.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-yellow-600 text-sm">💰</span>
                </div>
                <span className="text-sm text-gray-700 font-poppins">Smart investment guidance</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 text-sm">📈</span>
                </div>
                <span className="text-sm text-gray-700 font-poppins">Real-time gold market insights</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-sm">🤖</span>
                </div>
                <span className="text-sm text-gray-700 font-poppins">24/7 AI-powered support</span>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={closeWelcomeModal}
                className="w-full py-3 font-poppins bg-[#0b2436] text-white rounded-full font-semibold border-2 border-[#cfb484] transition-all duration-200 shadow-lg"
              >
                Start Conversation With Sona
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (showFeedbackModal) {
    return (
      <div className='bg-[#0b2436] h-screen'>
        {FeedbackModal()}
        <div className="flex flex-col h-full bg-gradient-to-br from-blue-50 to-indigo-100 md:w-1/2 m-auto opacity-50">
          {/* Dimmed chat interface in background */}
          <div className="sticky top-0 z-10 bg-white shadow-lg border-b border-gray-200 p-4">
            <div className="flex items-center space-x-3">
              <div className="w-14 h-14 rounded-full flex items-center justify-center">
                <img src={chatdp} alt="Fiydaa AI" className="w-12 h-12 object-contain" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800 font-poppins">Fiyda-Ai</h1>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <p className="text-sm text-gray-500 font-poppins">Limit Reached</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-600 font-poppins">Please provide your feedback to continue</p>
          </div>
        </div>
      </div>
    );
  }

  // Intro Screen
  if (showIntro) {
    return (
      <div className='bg-[#0b2436] h-screen'>
        {showWelcomeModal && <WelcomeModal />}
        <div className="flex flex-col h-full bg-gradient-to-br from-blue-50 to-indigo-100 md:w-1/2 m-auto">
          <div className="bg-white shadow-lg border-b border-gray-200 p-4">
            <div className="flex items-center space-x-3">
              <div className="w-14 h-14 rounded-full flex items-center justify-center">
                <button
                  onClick={() => window.open("https://www.fiydaa.in", "_blank")}
                // className="focus:outline-none"
                >
                  <img src={chatdp} alt="Fiydaa AI" className="w-12 h-12 object-contain" />
                </button>

              </div>
              <div>
                <h1 className="text-[18px] font-bold text-gray-800 font-poppins">SONA : <span className='text-[16px]'>Financial Assistant</span></h1>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <p className="text-sm text-gray-500 font-poppins">Online</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center p-6 font-poppins">
            <div className="w-28 h-28 rounded-full flex items-center justify-center shadow-lg bg-white">
              <img src={chatlogo} alt="Fiydaa Logo" className="w-20 h-20 object-contain" />
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-10 text-center">Welcome to Sona</h2>
            <p className="text-gray-600 text-center mb-2 px-1">
              New to gold investing? Let Sona guide you.
            </p>
            <p className="text-gray-600 text-center mb-2 px-1">
              Discover how digital gold works, start a SIP, or earn monthly income through gold
            </p>

            <div className="w-full max-w-sm">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 mt-5 text-center">Frequently asked</h3>

              <div className="space-y-3 mb-8">
                {initialQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => startChatWithQuestion(question)}
                    className="w-full px-4 py-3 bg-[#0b2436] text-white rounded-full transition-all duration-200 font-medium shadow-md text-sm"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white border-t border-gray-200 p-4">
            <div className="flex space-x-3">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type your query..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (inputText.trim()) {
                      startChatWithQuestion(inputText);
                      setInputText('');
                    }
                  }
                }}
                maxLength={200}
              />
              <button
                onClick={() => {
                  if (inputText.trim()) {
                    startChatWithQuestion(inputText);
                    setInputText('');
                  } else {
                    startChatManually();
                  }
                }}
                className="px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex items-center space-x-2"
              >
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">Send</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Chat Screen
  return (
    <div className='bg-[#0b2436] h-screen'>
      <div className="flex flex-col h-full bg-gradient-to-br from-blue-50 to-indigo-100 md:w-1/2 m-auto">
        {/* Header - Sticky */}
        <div className="sticky top-0 z-10 bg-white shadow-lg border-b border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="w-14 h-14 rounded-full flex items-center justify-center">
              <button
                onClick={() => window.open("https://www.fiydaa.in", "_blank")}
              // className="focus:outline-none"
              >
                <img src={chatdp} alt="Fiydaa AI" className="w-12 h-12 object-contain" />
              </button>
            </div>
            <div>
              <h1 className="text-[18px] font-bold text-gray-800 font-poppins">SONA : <span className='text-[16px]'>Financial Assistant</span></h1>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <p className="text-sm text-gray-500 font-poppins">Online</p>
              </div>
            </div>
          </div>
        </div>

        {/* Messages - Scrollable area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 font-poppins">
          {messages.map((message) => (
            <div key={message.id}>
              <div className={`flex ${message.sender == 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-start space-x-2 max-w-xs lg:max-w-md ${message.sender == 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${message.sender == 'user'
                    ? 'bg-[#0b2436]'
                    : 'bg-none'
                    }`}>
                    {message.sender == 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <img src={chatdp} alt="Fiydaa AI" className="w-6 h-6 object-contain" />
                    )}
                  </div>
                  <div className={`px-4 py-2 rounded-2xl shadow-sm font-poppins ${message.sender == 'user'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                    : 'bg-white text-gray-800 border border-gray-200'
                    }`}>
                    <div className="text-sm whitespace-pre-line">{message.text}</div>
                    <p className={`text-xs mt-1 ${message.sender == 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                      {formatTime(message.timestamp)}
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons - Updated to handle new format */}
              {message.sender === 'assistant' && message.ctaButtons && message.ctaButtons.length > 0 && (
                <div className="flex justify-start mt-1.5">
                  <div className="ml-10 space-y-1">
                    {message.ctaButtons.map((cta, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          // Google Analytics event tracking
                          if (typeof window !== 'undefined' && window.gtag) {
                            window.gtag('event', 'cta_button_click', {
                              event_category: 'engagement',
                              event_label: cta.text,
                              button_text: cta.text,
                              button_position: index + 1,
                              navigation_target: cta.navigation,
                              // Optional: Add more context
                              message_id: message.id || 'unknown',
                              timestamp: new Date().toISOString()
                            });

                            // Also send a conversion event if this is important for your business
                            window.gtag('event', 'conversion', {
                              send_to: 'G-700YYPMMEP/cta_click',
                              event_category: 'cta_engagement',
                              event_label: cta.text
                            });
                          }

                          // Your existing click handler
                          handleClick(cta.navigation);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-[#cfb484] text-white rounded-full transition-all duration-200 text-sm font-medium shadow-md"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>{cta.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Follow-up Questions - Always show initially, hide permanently after interaction */}
              {message.sender === 'assistant' &&
                message.followupQuestions &&
                message.followupQuestions.length > 0 &&
                !hiddenFollowUps.has(message.id) && (
                  <div className="flex justify-end mt-2">
                    <div className="mr-2 space-y-2 max-w-xs">
                      <p className="text-xs text-blue-600 hover:text-blue-800 text-right mr-2 font-bold mt-4">Related questions</p>
                      <div className="space-y-2">
                        {message.followupQuestions.map((question, index) => (
                          <button
                            key={index}
                            onClick={() => handleFollowUpClick(question, message.id)}
                            className="block w-full text-left px-3 py-2.5 bg-[#0b2436]  text-white rounded-full text-xs border-2 border-[#cfb484] transition-colors duration-200"
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="flex items-start space-x-2 max-w-xs lg:max-w-md">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                  <img src={chatdp} alt="Fiydaa AI" className="w-6 h-6 object-contain" />
                </div>
                <div className="px-4 py-2 rounded-2xl bg-white border border-gray-200">
                  {isFirstChat ? (
                    // First chat - show "Setting up conversation"
                    <div className="text-gray-600 text-sm">
                      Setting up conversation...
                    </div>
                  ) : (
                    // Subsequent chats - show animated dots
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input - Sticky at bottom */}
        <div className="sticky bottom-0 z-10 bg-white border-t border-gray-200 p-4 font-poppins">
          <div className="flex space-x-3">
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              maxLength={200}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputText.trim() || isTyping}
              className="px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-2"
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Send</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FiydaaChatBot;
